/**
 * RewardClaimSheet — Plan 02-12 Task 1 / UI-SPEC §6.
 *
 * BottomSheet wrapper that confirms a reward redemption:
 *   Heading: "Bytt {{cost}} p for {{title}}?"
 *   Description: reward.description (optional)
 *   Confirm: "Ja, bytt" (terracotta) → POST /api/rewards/:id/claim
 *   Cancel:  "Avbryt" → onClose
 *
 * Error UX (CRIT-1 v1 surface):
 *   - 422 insufficient_points → inline error
 *     "Du mangler {{N}} p — fortsett å samle!" computed from the
 *     server's body.balance vs reward.point_cost.
 *   - 422 reward_allergy_blocked → inline collision copy (Layer-3 race).
 *
 * On success: optimistic prepend handled inside useClaimReward; the
 * sheet closes via onClose + emits a sage toast "Sendt til foreldrene
 * for godkjenning".
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { toast } from '../../lib/toast';
import { useClaimReward } from './useRewardsQueries';
import type { RewardRow } from './useRewardsQueries';

interface RewardClaimSheetProps {
  reward: RewardRow;
  viewerId: string;
  /** Caller's current point balance — used to compute "Du mangler N p". */
  balance: number;
  open: boolean;
  onClose: () => void;
}

export function RewardClaimSheet({
  reward,
  viewerId,
  balance,
  open,
  onClose,
}: RewardClaimSheetProps): ReactNode {
  const { t } = useTranslation();
  const claim = useClaimReward(viewerId);
  const [errorBody, setErrorBody] = useState<{
    code: string;
    detail: string;
  } | null>(null);

  const handleConfirm = (): void => {
    setErrorBody(null);
    claim.mutate(reward.id, {
      onSuccess: () => {
        toast({ kind: 'success', body: t('rewards.success.sent') });
        onClose();
      },
      onError: (err) => {
        const body = err.body as
          | { error?: string; balance?: number; cost?: number; matched_keyword?: string }
          | undefined;
        const code = body?.error ?? 'unknown';
        if (code === 'insufficient_points') {
          // Prefer the server's response (authoritative) over the prop;
          // fall back to the client-computed delta.
          const serverCost = body?.cost ?? reward.point_cost;
          const serverBalance = body?.balance ?? balance;
          const need = Math.max(0, serverCost - serverBalance);
          setErrorBody({
            code,
            detail: t('rewards.error.insufficient', { count: need }),
          });
        } else if (code === 'reward_allergy_blocked') {
          setErrorBody({
            code,
            detail: t('rewards.error.allergy'),
          });
        } else {
          setErrorBody({
            code,
            detail: t('rewards.error.generic'),
          });
        }
      },
    });
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('rewards.confirm.heading', {
        cost: reward.point_cost,
        title: reward.title,
      })}
    >
      <div data-testid="reward-claim-sheet" className="flex flex-col gap-4">
        {reward.description ? (
          <p className="text-ink-2 text-sm">{reward.description}</p>
        ) : null}

        {errorBody ? (
          <p
            data-testid="reward-claim-error"
            className="rounded-md border border-bad bg-raised px-3 py-2 text-sm text-ink"
          >
            {errorBody.detail}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="reward-claim-cancel"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-pill border border-card-line bg-raised text-ink px-4 py-2 text-sm font-semibold"
          >
            {t('rewards.confirm.cancel')}
          </button>
          <button
            type="button"
            data-testid="reward-claim-confirm"
            disabled={claim.isPending}
            onClick={handleConfirm}
            className="inline-flex items-center justify-center rounded-pill bg-primary text-on-primary px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {claim.isPending ? (
              <span
                data-testid="reward-claim-confirm-spinner"
                aria-hidden
                className="inline-block h-3 w-3 animate-pulse rounded-full bg-sheet"
              />
            ) : (
              t('rewards.confirm.primary')
            )}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
